import React, { useMemo, useRef, useState, useEffect } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { X, Save, Eye, Upload, Loader2 } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { adminBlogGet, adminBlogCreate, adminBlogUpdate, uploadAdminFile, fileUrl } from "../api";

const field = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20";
const labelCls = "block text-xs font-semibold text-[#0D1B2A] mb-1 uppercase tracking-wide";

const empty = {
  title: "", slug: "", cover_image: "", excerpt: "", author: "PhotonPE Team",
  category: "General", tags: [], body: "", seo_title: "", seo_description: "",
  featured: false, published: true, published_date: "",
};

const BlogEditor = ({ postId, onClose, onSaved }) => {
  const { toast } = useToast();
  const [data, setData] = useState(empty);
  const [tagsStr, setTagsStr] = useState("");
  const [loading, setLoading] = useState(!!postId);
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [preview, setPreview] = useState(false);
  const quillRef = useRef(null);

  useEffect(() => {
    if (postId) {
      adminBlogGet(postId).then((p) => {
        setData({ ...empty, ...p });
        setTagsStr((p.tags || []).join(", "));
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  }, [postId]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  // Quill image upload handler -> uploads to backend, inserts returned URL
  const imageHandler = () => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();
    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;
      const fd = new FormData();
      fd.append("file", file);
      try {
        const res = await uploadAdminFile(fd);
        const editor = quillRef.current.getEditor();
        const range = editor.getSelection(true);
        editor.insertEmbed(range.index, "image", fileUrl(res.url));
        editor.setSelection(range.index + 1);
      } catch {
        toast({ title: "Image upload failed" });
      }
    };
  };

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ align: [] }],
        ["blockquote", "code-block"],
        ["link", "image", "video"],
        ["clean"],
      ],
      handlers: { image: imageHandler },
    },
  }), []);

  const uploadCover = async (file) => {
    if (!file) return;
    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await uploadAdminFile(fd);
      set("cover_image", res.url);
      toast({ title: "Cover uploaded" });
    } catch {
      toast({ title: "Cover upload failed" });
    } finally {
      setUploadingCover(false);
    }
  };

  const save = async () => {
    if (!data.title) { toast({ title: "Title is required" }); return; }
    setSaving(true);
    const payload = { ...data, tags: tagsStr.split(",").map((t) => t.trim()).filter(Boolean) };
    try {
      if (postId) await adminBlogUpdate(postId, payload);
      else await adminBlogCreate(payload);
      toast({ title: "Article saved 🎉" });
      onSaved();
    } catch {
      toast({ title: "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-white rounded-2xl w-full max-w-5xl my-6 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white rounded-t-2xl z-10">
          <h2 className="text-lg font-bold text-[#0D1B2A]">{postId ? "Edit Article" : "New Article"}</h2>
          <div className="flex items-center gap-2">
            <button onClick={() => setPreview((p) => !p)} className="flex items-center gap-1.5 text-sm font-semibold text-[#0EA5E9] px-3 py-2 rounded-lg hover:bg-[#F0FDFA]">
              <Eye size={15} /> {preview ? "Editor" : "Preview"}
            </button>
            <button onClick={save} disabled={saving} className="flex items-center gap-1.5 text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-70" style={{ background: "linear-gradient(90deg,#06B6D4,#10B981)" }}>
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
            </button>
            <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-slate-100"><X size={18} /></button>
          </div>
        </div>

        {loading ? (
          <div className="p-16 text-center text-[#475569]">Loading…</div>
        ) : preview ? (
          <div className="p-8">
            {data.cover_image && <img src={fileUrl(data.cover_image)} alt="" className="w-full h-64 object-cover rounded-2xl mb-6" />}
            <span className="text-xs font-semibold text-[#06B6D4]">{data.category}</span>
            <h1 className="text-3xl font-extrabold text-[#0D1B2A] mt-2 mb-4">{data.title || "Untitled"}</h1>
            <div className="pp-article" dangerouslySetInnerHTML={{ __html: data.body }} />
          </div>
        ) : (
          <div className="p-6 grid lg:grid-cols-[1fr_300px] gap-6">
            {/* Main */}
            <div className="space-y-4">
              <div><label className={labelCls}>Title</label><input className={field} value={data.title} onChange={(e) => set("title", e.target.value)} placeholder="Article title" /></div>
              <div><label className={labelCls}>Slug (optional)</label><input className={field} value={data.slug} onChange={(e) => set("slug", e.target.value)} placeholder="auto-generated-from-title" /></div>
              <div><label className={labelCls}>Excerpt</label><textarea className={field} rows={2} value={data.excerpt} onChange={(e) => set("excerpt", e.target.value)} /></div>
              <div>
                <label className={labelCls}>Article Body</label>
                <div className="pp-quill">
                  <ReactQuill ref={quillRef} theme="snow" value={data.body} onChange={(v) => set("body", v)} modules={modules} />
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-4">
              <div className="rounded-xl border border-slate-100 p-4 space-y-3">
                <label className="flex items-center justify-between text-sm font-semibold text-[#0D1B2A]">
                  Published
                  <input type="checkbox" className="w-4 h-4 accent-[#06B6D4]" checked={data.published} onChange={(e) => set("published", e.target.checked)} />
                </label>
                <label className="flex items-center justify-between text-sm font-semibold text-[#0D1B2A]">
                  Featured
                  <input type="checkbox" className="w-4 h-4 accent-[#06B6D4]" checked={data.featured} onChange={(e) => set("featured", e.target.checked)} />
                </label>
              </div>

              <div>
                <label className={labelCls}>Cover Image</label>
                {data.cover_image && <img src={fileUrl(data.cover_image)} alt="" className="w-full h-28 object-cover rounded-lg mb-2" />}
                <label className="flex items-center justify-center gap-2 text-sm font-semibold text-[#0EA5E9] border border-dashed border-slate-300 rounded-lg py-3 cursor-pointer hover:border-[#0EA5E9]">
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadCover(e.target.files[0])} />
                  {uploadingCover ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload
                </label>
                <input className={`${field} mt-2`} value={data.cover_image} onChange={(e) => set("cover_image", e.target.value)} placeholder="or paste image URL" />
              </div>

              <div><label className={labelCls}>Author</label><input className={field} value={data.author} onChange={(e) => set("author", e.target.value)} /></div>
              <div><label className={labelCls}>Category</label><input className={field} value={data.category} onChange={(e) => set("category", e.target.value)} /></div>
              <div><label className={labelCls}>Tags (comma separated)</label><input className={field} value={tagsStr} onChange={(e) => setTagsStr(e.target.value)} /></div>
              <div><label className={labelCls}>SEO Title</label><input className={field} value={data.seo_title} onChange={(e) => set("seo_title", e.target.value)} /></div>
              <div><label className={labelCls}>SEO Description</label><textarea className={field} rows={3} value={data.seo_description} onChange={(e) => set("seo_description", e.target.value)} /></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogEditor;
